/* eslint quotes: 0 */
import {
  Button,
  Card,
  Col,
  Modal,
  Popover,
  Row,
  Space,
  Spin,
  Table,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import React, { FunctionComponent, useEffect, useState } from "react";

import {
  fetchSavedSurveys,
  fetchSurveyResponses,
} from "../../lib/surveyQueries";
import DotsFishLoading from "../DotsFishLoading/DotsFishLoading";
import {
  getSavedSurveysColumns,
  getSurveyResponsesColums,
} from "./SurveyResultsTab.columns";
import SavedSurvey, { SurveyResponse } from "./SurveyResultsTab.type";

const dateTimeFormat = "D, MMM, kk, mm";

/**
 * Component for displaying a survey results table on the Message section.
 */
const SurveyResultsTab: FunctionComponent = () => {
  const teamId = localStorage.getItem("team-id");
  const [isDataSetEmpty, setIsDataSetEmpty] = useState(false);
  const [isPreviewVisible, setPreviewVisible] = useState(false);
  const [savedSurveys, setSavedSurveys] = useState<SavedSurvey[]>([]);
  const [savedSurveysColumns, setSavedSurveysColumns] = useState<
    ColumnsType<SavedSurvey>
  >([]);
  const [surveyResponses, setSurveyResponses] = useState<SurveyResponse[]>();
  const [surveyResponsesColumns, setSurveyResponsesColumns] = useState<
    ColumnsType<SurveyResponse>
  >([]);

  // console.log("surveyResponses", surveyResponses);
  // console.log("surveyResponsesColumns", surveyResponsesColumns);
  // console.log("savedSurveys", savedSurveys);
  console.log("surveyResponses", surveyResponses);

  const displaySurveyResponsesPreview = async (survey: SavedSurvey) => {
    if (survey) {
      const onboardingQuestions =
        survey?.survey_content?.onboarding_questions || [];
      if (onboardingQuestions[0]?.question_type === "message")
        onboardingQuestions.splice(0, 1);
      const surveyResponse = await fetchSurveyResponses(survey.id);
      const surveyData: any = {};
      surveyResponse?.forEach((element) => {
        const data = surveyData[element.slack_user];
        if (data) {
          data.responses.push({
            created_at: element.created_at,
            question_id: element.question_id,
            response: element.response,
          });
        } else {
          surveyData[element.slack_user] = {
            responses: [
              {
                created_at: element.created_at,
                question_id: element.question_id,
                response: element.response,
              },
            ],
            slack_user: element.slack_user,
            slack_users: element.slack_users,
            survey_id: element.survey_id,
            team: element.team,
            workspace: element.workspace,
          };
        }
      });
      const surveyDataArray: SurveyResponse[] = Object.values(surveyData);
      const columns: ColumnsType<SurveyResponse> = [];
      onboardingQuestions.forEach((onboardingQuestion, index) => {
        columns.push({
          title: (
            <Popover content={`${index + 1}. ${onboardingQuestion.question}`}>
              <p className="truncate">{`${index + 1}. ${
                onboardingQuestion.question
              }`}</p>
            </Popover>
          ),
          dataIndex: "responses.question_id",
          key: onboardingQuestion.id,
          width: 200,
          render: (_, record: any) => {
            const questionResponses = record.responses.filter(
              (response: { question_id: any }) =>
                response.question_id === onboardingQuestion.id
            );
            const displayText = questionResponses
              .map((field: any, counter: number) =>
                counter + 1 === questionResponses.length
                  ? `${field.response.display}`
                  : `${field.response.display}, `
              )
              .join("");
            return (
              <Popover content={displayText}>
                <p className="truncate">{displayText}</p>
              </Popover>
            );
          },
        });
      });
      setSurveyResponsesColumns((state) => [...state, ...columns]);
      setSurveyResponses(surveyDataArray);
      setPreviewVisible(true);
    } else {
      setPreviewVisible(false);
    }
  };

  const clearPreview = () => {
    setSurveyResponses(undefined);
    setSurveyResponsesColumns(getSurveyResponsesColums());
    setPreviewVisible(false);
  };

  const fetchPageData = async () => {
    const survey = await fetchSavedSurveys(teamId || "");

    if (survey?.length) {
      setSavedSurveys(survey);
      setSavedSurveysColumns(
        getSavedSurveysColumns(displaySurveyResponsesPreview, fetchPageData)
      );
      setSurveyResponsesColumns(getSurveyResponsesColums());
    } else {
      setIsDataSetEmpty(true);
    }
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  /**
   *
   * @param dateString : string - A string representing the datetime in the format "D, MMM, kk, mm"
   * @returns The formated date for CSV exports
   */
  const formatDate = (dateString: string) => {
    const dateArray = dateString.split(", ");
    return `${dateArray[0]}-${dateArray[1]} ${dateArray[2]}:${dateArray[3]}`;
  };

  /**
   * Exports a survey's responses to a CSV file
   */
  function exportResponses() {
    const answers: any = [];
    let questions: any = {};
    surveyResponses?.forEach((surveyResponse: any) => {
      const currentSurvey: any = savedSurveys.filter(
        (survey: any) => survey.id === surveyResponse.survey_id
      )[0];

      // For each onboarding question in the survey, assign the correct responses
      const totalQuestions =
        currentSurvey.survey_content.onboarding_questions.length;
      currentSurvey.survey_content.onboarding_questions.forEach(
        (question: any, index: number) => {
          const { responses } = surveyResponse;
          const firstResponseDate = responses[0].created_at;
          const assignedResponses = responses.filter(
            (item: any) => item.question_id === question.id
          );
          const cleanResponses = assignedResponses
            .map((item: any) => item.response.display)
            .join(", ");
          const member = surveyResponse.slack_users.name;
          const formatedDate = formatDate(
            moment(firstResponseDate).format(dateTimeFormat)
          );

          if (totalQuestions - 1 === index) {
            questions = {
              time: formatedDate,
              member,
              ...questions,
              [question.question.split(",").join("").split('"').join("")]:
                cleanResponses,
            };
          }
          questions = {
            ...questions,
            time: formatedDate, // time must go here, otherwise it's overwritten
            member, // member must go here, otherwise it's overwritten
            [question.question.split(",").join("").split('"').join("")]:
              cleanResponses,
          };
        }
      );
      answers.push(questions);
    });

    const csvOptions = {
      fieldSeparator: ",",
      quoteStrings: '"', // eslint-disable-line
      decimalSeparator: ".",
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      fileName: "Exported survey responses",
      // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
    };

    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(answers);
  }

  return (
    <div>
      <Row justify="space-around" gutter={[32, 32]}>
        <Col lg={24} md={24}>
          <Card bordered={false}>
            <div style={{ width: "100%", height: "100%" }}>
              {savedSurveys.length ? (
                <Table
                  dataSource={savedSurveys}
                  columns={savedSurveysColumns}
                />
              ) : (
                <div className="flex flex-1 items-center justify-center">
                  <DotsFishLoading />
                </div>
              )}
              {isDataSetEmpty && (
                <div className="items-center flex justify-center">
                  <p>
                    <a
                      className="cursor-pointer no-underline text-black"
                      href="https://faq.dots.community/help/sending-surveys-to-members"
                    >
                      No surveys created! Don&apos;t know how to send a survey?
                      Click here
                    </a>
                  </p>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
      <Modal
        bodyStyle={{ overflowX: "scroll" }}
        footer={null}
        onCancel={clearPreview}
        visible={isPreviewVisible}
        width={1000}
      >
        <h1 className="mb-4">Survey Results</h1>
        <Space />
        <Table
          className="w-full"
          columns={surveyResponsesColumns}
          dataSource={surveyResponses}
          scroll={{ x: 900 }}
          footer={() => (
            <Popover title="Click to export to CSV. Please reach out to us if you want another format">
              <Button onClick={() => exportResponses()}>Export</Button>
            </Popover>
          )}
        />
      </Modal>
    </div>
  );
};

export default SurveyResultsTab;
